// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Registered colleges and their lifecycle (**ADR-0076**, **ADR-0067**).
 *
 * `docs/04-REPOSITORY_GUIDE.md` names this area `tenants/`; "College" is the
 * product vocabulary and stays in the column headers and the controls.
 *
 * **Four lifecycles, four columns.** ADR-0076 §1 separates the tenant status,
 * the activation-code state, and the onboarding state precisely because
 * conflating them produced "claimed therefore active". The table shows all
 * three side by side and derives none of them from another, so a college whose
 * code has been claimed still reads *Registered — pending onboarding* until
 * verified readiness evidence makes it active.
 *
 * **One read for the whole table.** The v2 college overview carries the
 * activation and onboarding summaries with each row, so nothing here issues a
 * per-row request. The onboarding *detail* read is a separate, capability-gated
 * operation and fires only when an operator opens that dialog — a table that
 * fetched it per row would be an N+1 against the control plane for information
 * the row already had.
 *
 * **Capabilities are checked one at a time, and none is inferred.** There are
 * six operations and six capability checks. Nothing here reads a role: the
 * demo administrator holds `university_admin`, and a screen that treated that
 * name as a grant would offer every capability added later to every session
 * already carrying it. Hiding a control is a usability affordance and never an
 * authorization decision (`CST-048`, `ADM-014`, `07-SECURITY.md` §15.4) — the
 * BFF and Platform authorize each request independently, a refusal is expected
 * rather than impossible, and {@link describeLifecycleFailure} is what makes
 * one legible.
 *
 * **The one-time activation code lives in this component's state and nowhere
 * else.** It arrives in exactly one response body (**ADR-0076** §3), is shown
 * once, and is discarded when the dialog closes, when the operator navigates
 * away, when the session ends, or when this screen unmounts. It is never
 * written to browser storage, a cookie, the URL, the history, or the console.
 * A disclosure is set *before* the list is re-read, so a failing refetch cannot
 * take away a code the operator has not yet copied.
 */

import {
  AsyncSection,
  capabilities,
  DataTable,
  hasCapability,
  PageHeader,
  queryKeys,
  RequireCapability,
  StateBadge,
  useApiClients,
  type EdgeProvisioningView,
  useSession,
  type DataTableColumn,
  type TenantCreateResult,
  type TenantOverview,
} from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useControlPlaneQuery } from '../app-shell';
import {
  ActivationDisclosureDialog,
  type ActivationDisclosure,
} from './ActivationDisclosureDialog';
import { AddCollegePanel } from './AddCollegePanel';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { EditCollegeDialog, type CollegeAmendment } from './EditCollegeDialog';
import { OnboardingDetailsDialog } from './OnboardingDetailsDialog';
import {
  ACTIVATION_STATE_PRESENTATION,
  describeExpiry,
  describeLifecycleFailure,
  canProvisionFor,
  describeEdgeFailure,
  describeEdgeProvisioning,
  describeOnboarding,
  isDeactivatable,
  isRotatable,
  isDeletable,
  isFirstIssuance,
  maskedTail,
  rotateActionLabel,
  TENANT_STATUS_PRESENTATION,
  type LifecycleOperation,
} from './presentation';

/** Which per-college dialog is open, and for which college. */
type OpenDialog =
  | { readonly kind: 'edit'; readonly college: TenantOverview }
  | { readonly kind: 'deactivate'; readonly college: TenantOverview }
  | { readonly kind: 'delete'; readonly college: TenantOverview }
  | { readonly kind: 'onboarding'; readonly college: TenantOverview }
  | null;

/** Where a sanitised refusal is shown. */
type FailureTarget = 'screen' | 'dialog';

/**
 * A disclosure, together with the screen it belongs to.
 *
 * The path is carried so the code can be shown *only* while the screen that
 * obtained it is the one on display. It is not navigation state and never
 * reaches a URL.
 */
interface HeldDisclosure extends ActivationDisclosure {
  readonly pathname: string;
}

/** Registered-college registry and lifecycle screen. */
export function TenantsScreen(): ReactNode {
  const { session, scope } = useSession();
  const clients = useApiClients();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Six independent checks. Deliberately not folded into one "can manage"
  // boolean: Platform split these scopes because holding one is not evidence
  // for holding another.
  const canCreate = hasCapability(session, capabilities.tenantsCreate);
  const canUpdate = hasCapability(session, capabilities.tenantsUpdate);
  const canDeactivate = hasCapability(session, capabilities.tenantsDeactivate);
  const canDelete = hasCapability(session, capabilities.tenantsDelete);
  const canRotate = hasCapability(session, capabilities.tenantsActivationRotate);
  const canReadOnboarding = hasCapability(session, capabilities.tenantsOnboardingRead);
  const canProvisionEdge = hasCapability(session, capabilities.tenantsEdgeProvision);

  const query = useControlPlaneQuery(
    (cacheScope) => queryKeys.controlPlane.collegeOverview(cacheScope),
    (client, signal) => client.listCollegeOverview({ signal }),
  );

  // States that mean the allocation is still moving, so the row is worth
  // re-reading. Everything else has settled and is refreshed by acting on it.
  // Declared outside the render so the identity is stable.
  // (see IN_FLIGHT_STATES below the component)
  // One request per visible college. The list endpoint carries registration,
  // activation and onboarding; the allocation is a different lifecycle on a
  // different cluster, so it is read separately rather than widening a
  // read model that three screens already share.
  const rows = query.data?.items ?? [];
  const edgeQueries = useQueries({
    queries: rows.map((row) => ({
      queryKey: ['control-plane', 'edge-provisioning', row.id] as const,
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        clients.controlPlane.readEdgeProvisioning({ tenantId: row.id, signal }),
      enabled: canProvisionEdge,
    })),
  });
  const edgeByTenant: Record<string, EdgeProvisioningView | null> = {};
  rows.forEach((row, index) => {
    edgeByTenant[row.id] = edgeQueries[index]?.data ?? null;
  });

  // Re-read only while something is moving. A settled fleet is refreshed by
  // acting on it, not on a timer: polling colleges that have finished is load
  // with no question behind it.
  const watching = rows.some((row) => IN_FLIGHT_STATES.includes(edgeByTenant[row.id]?.state ?? ''));
  useEffect(() => {
    if (!watching) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      edgeQueries.forEach((entry: { refetch: () => Promise<unknown> }) => {
        void entry.refetch();
      });
    }, 3000);
    return () => {
      window.clearInterval(timer);
    };
  }, [watching, edgeQueries]);

  const [heldDisclosure, setHeldDisclosure] = useState<HeldDisclosure | null>(null);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [screenFailure, setScreenFailure] = useState<string | null>(null);
  const [dialogFailure, setDialogFailure] = useState<string | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);

  // The in-flight operation is mirrored in a ref because the guard has to hold
  // within a single event loop turn. Two clicks dispatched before React
  // re-renders would both read the same `pending` state and both proceed.
  const pendingRef = useRef<string | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const authenticated = session?.authenticated === true;

  // --- the one-time code is discarded on every exit from this screen --------
  //
  // Discarding it is what unmounting already does: a route change swaps the
  // routed element, and a session that ends puts the gate's sign-in screen in
  // place of this whole tree. Either way the state holding the plaintext is
  // released with the component.
  //
  // The derivation below is the guard for anything that would keep this screen
  // mounted across one of those events. It is computed during render rather
  // than repaired afterwards in an effect — an effect would leave the code
  // rendered for the frame before it ran, which for a credential is the one
  // frame that matters.
  const disclosure: ActivationDisclosure | null =
    heldDisclosure !== null && heldDisclosure.pathname === location.pathname && authenticated
      ? heldDisclosure
      : null;

  // Focus returns to the control that opened the dialog, once nothing is open.
  // Both dialog kinds are considered, so closing one to open another does not
  // strand focus on the body.
  useEffect(() => {
    if (disclosure === null && dialog === null && returnFocusRef.current !== null) {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      target.focus();
    }
  }, [disclosure, dialog]);

  /** Re-read the canonical list, recording rather than throwing a failure. */
  const refresh = useCallback(async (): Promise<void> => {
    // A cached onboarding detail describes the state before this mutation, so
    // it is dropped rather than served again. Removing beats invalidating:
    // there is no observer while the dialog is closed, so an invalidated entry
    // would sit stale until something happened to mount one.
    queryClient.removeQueries({
      queryKey: [scope, 'control-plane', 'college-onboarding'],
      exact: false,
    });
    const result = await query.refetch();
    setRefreshFailed(result.isError);
  }, [query, queryClient, scope]);

  /**
   * Run one lifecycle operation, once.
   *
   * The refusal is sanitised here rather than at each call site, so no caller
   * can accidentally render an upstream message.
   */
  const runAction = useCallback(
    async (
      key: string,
      operation: LifecycleOperation,
      perform: () => Promise<void>,
      target: FailureTarget,
    ): Promise<void> => {
      if (pendingRef.current !== null) {
        return;
      }
      pendingRef.current = key;
      setPending(key);
      setScreenFailure(null);
      setDialogFailure(null);
      try {
        await perform();
      } catch (error) {
        const text = describeLifecycleFailure(error, operation);
        if (target === 'dialog') {
          setDialogFailure(text);
        } else {
          setScreenFailure(text);
        }
      } finally {
        pendingRef.current = null;
        setPending(null);
      }
    },
    [],
  );

  const closeDialog = useCallback((): void => {
    setDialog(null);
    setDialogFailure(null);
  }, []);

  const closeDisclosure = useCallback((): void => {
    // Clearing the state unmounts the dialog, which is what removes the
    // plaintext from both component state and the DOM at once. There is no
    // second copy to clear.
    setHeldDisclosure(null);
  }, []);

  const openDisclosure = useCallback(
    (
      collegeLabel: string,
      issuance: ActivationDisclosure['issuance'],
      previousCodeRevoked: boolean,
      initiator: HTMLElement | null,
    ): void => {
      returnFocusRef.current = initiator;
      setHeldDisclosure({
        collegeLabel,
        issuance,
        previousCodeRevoked,
        pathname: location.pathname,
      });
    },
    [location.pathname],
  );

  const handleRegistered = useCallback(
    async (result: TenantCreateResult, initiator: HTMLElement | null): Promise<void> => {
      // Disclosed first, refreshed second. The order is the whole guarantee:
      // the code exists in one response body and cannot be asked for again, so
      // it must not depend on a subsequent request succeeding.
      openDisclosure(result.name, result.activation, false, initiator);
      await refresh();
    },
    [openDisclosure, refresh],
  );

  const rotate = useCallback(
    async (row: TenantOverview, initiator: HTMLElement | null): Promise<void> => {
      await runAction(
        `generate:${row.id}`,
        'generate',
        async () => {
          const result = await clients.controlPlane.rotateActivationCode({ tenantId: row.id });
          openDisclosure(row.name, result.activation, result.previous_code_revoked, initiator);
          await refresh();
        },
        'screen',
      );
    },
    [clients, openDisclosure, refresh, runAction],
  );

  const provisionEdge = useCallback(
    async (row: TenantOverview): Promise<void> => {
      await runAction(
        `provision:${row.id}`,
        'provision',
        async () => {
          await clients.controlPlane.requestEdgeProvisioning({ tenantId: row.id });
          // Refetch immediately rather than waiting for the poll, so the row
          // moves the moment the operator acts.
          await Promise.all(
            edgeQueries.map((entry: { refetch: () => Promise<unknown> }) => entry.refetch()),
          );
        },
        'screen',
      );
    },
    [clients, edgeQueries, runAction],
  );

  const amend = useCallback(
    async (college: TenantOverview, amendment: CollegeAmendment): Promise<void> => {
      await runAction(
        `amend:${college.id}`,
        'amend',
        async () => {
          await clients.controlPlane.amendCollege({
            tenantId: college.id,
            name: amendment.name,
            address: amendment.address,
          });
          setDialog(null);
          await refresh();
        },
        'dialog',
      );
    },
    [clients, refresh, runAction],
  );

  const deactivate = useCallback(
    async (college: TenantOverview): Promise<void> => {
      await runAction(
        `deactivate:${college.id}`,
        'deactivate',
        async () => {
          await clients.controlPlane.deactivateCollege({ tenantId: college.id });
          setDialog(null);
          await refresh();
        },
        'dialog',
      );
    },
    [clients, refresh, runAction],
  );

  const remove = useCallback(
    async (college: TenantOverview): Promise<void> => {
      await runAction(
        `delete:${college.id}`,
        'delete',
        async () => {
          await clients.controlPlane.deleteCollege({ tenantId: college.id });
          setDialog(null);
          await refresh();
        },
        'dialog',
      );
    },
    [clients, refresh, runAction],
  );

  const openFor = useCallback(
    (next: NonNullable<OpenDialog>, initiator: HTMLElement | null): void => {
      returnFocusRef.current = initiator;
      setDialogFailure(null);
      setDialog(next);
    },
    [],
  );

  const busy = pending !== null;

  const columns: readonly DataTableColumn<TenantOverview>[] = [
    { key: 'name', header: 'College', render: (row) => row.name },
    { key: 'code', header: 'Code', render: (row) => row.code },
    {
      key: 'address',
      header: 'Address',
      render: (row) => (row.address === '' ? '—' : row.address),
    },
    {
      key: 'status',
      header: 'Tenant status',
      render: (row) => (
        <StateBadge
          label={TENANT_STATUS_PRESENTATION[row.status].label}
          tone={TENANT_STATUS_PRESENTATION[row.status].tone}
          subject={`${row.name} registration`}
        />
      ),
    },
    {
      key: 'activation',
      header: 'Activation',
      render: (row) => {
        const presentation = ACTIVATION_STATE_PRESENTATION[row.activation.state];
        const tail = maskedTail(row.activation);
        return (
          <Stack spacing={0.5}>
            <StateBadge
              label={presentation.label}
              tone={presentation.tone}
              subject={`${row.name} activation code`}
            />
            <Typography variant="body2" color="text.secondary">
              {tail ?? 'No code issued'}
            </Typography>
          </Stack>
        );
      },
    },
    { key: 'expiry', header: 'Expiry', render: (row) => describeExpiry(row.activation) },
    {
      key: 'onboarding',
      header: 'Onboarding',
      render: (row) => {
        const presentation = describeOnboarding(row.onboarding);
        return (
          <StateBadge
            label={presentation.label}
            tone={presentation.tone}
            subject={`${row.name} onboarding`}
          />
        );
      },
    },
    {
      key: 'edge',
      header: 'Edge',
      render: (row) => {
        const allocation = edgeByTenant[row.id] ?? null;
        const presentation = describeEdgeProvisioning(allocation);
        const failure = describeEdgeFailure(allocation);
        return (
          <Stack spacing={0.5}>
            <StateBadge
              label={presentation.label}
              tone={presentation.tone}
              subject={`${row.name} Edge`}
            />
            {failure === null ? null : (
              <Typography variant="caption" color="text.secondary">
                {failure}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {canRotate && isRotatable(row) ? (
            <Button
              size="small"
              disabled={busy}
              aria-label={`${rotateActionLabel(row)} for ${row.name}`}
              onClick={(event) => {
                void rotate(row, event.currentTarget);
              }}
            >
              {/* The short label and the accessible name are derived from one
                  predicate, so they cannot drift into disagreeing about
                  whether this is a first issuance. */}
              {isFirstIssuance(row) ? 'Generate' : 'Regenerate'}
            </Button>
          ) : null}
          {canUpdate ? (
            <Button
              size="small"
              disabled={busy}
              aria-label={`Edit name and address for ${row.name}`}
              onClick={(event) => {
                openFor({ kind: 'edit', college: row }, event.currentTarget);
              }}
            >
              Edit
            </Button>
          ) : null}
          {canDeactivate && isDeactivatable(row) ? (
            <Button
              size="small"
              disabled={busy}
              aria-label={`Deactivate ${row.name}`}
              onClick={(event) => {
                openFor({ kind: 'deactivate', college: row }, event.currentTarget);
              }}
            >
              Deactivate
            </Button>
          ) : null}
          {canDelete && isDeletable(row) ? (
            <Button
              size="small"
              color="error"
              disabled={busy}
              aria-label={`Delete the registration for ${row.name}`}
              onClick={(event) => {
                openFor({ kind: 'delete', college: row }, event.currentTarget);
              }}
            >
              Delete
            </Button>
          ) : null}
          {canProvisionEdge && canProvisionFor(row, edgeByTenant[row.id] ?? null) ? (
            <Button
              size="small"
              disabled={busy}
              aria-label={
                (edgeByTenant[row.id]?.can_retry ?? false)
                  ? `Retry provisioning the Edge appliance for ${row.name}`
                  : `Provision the Edge appliance for ${row.name}`
              }
              onClick={() => {
                void provisionEdge(row);
              }}
            >
              {(edgeByTenant[row.id]?.can_retry ?? false) ? 'Retry' : 'Provision Edge'}
            </Button>
          ) : null}
          {(edgeByTenant[row.id]?.setup_is_open ?? false) ? (
            <Button
              size="small"
              component="a"
              href={edgeByTenant[row.id]?.setup_url ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open the Setup Console for ${row.name}`}
            >
              Open setup
            </Button>
          ) : null}
          {canReadOnboarding ? (
            <Button
              size="small"
              disabled={busy}
              aria-label={`View onboarding details for ${row.name}`}
              onClick={(event) => {
                openFor({ kind: 'onboarding', college: row }, event.currentTarget);
              }}
            >
              Onboarding details
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <RequireCapability required={[capabilities.tenantsRead]} area="Tenants">
      <PageHeader
        title="Tenants"
        description="Colleges registered in the control plane, with their activation and onboarding state. Registration records routing and eligibility only; college credentials remain in the college data plane."
        {...(session?.university_label === null || session?.university_label === undefined
          ? {}
          : { scopeLabel: session.university_label })}
      />

      {canCreate ? <AddCollegePanel onRegistered={handleRegistered} /> : null}

      {screenFailure === null ? null : (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} data-testid="tenants-error">
          {screenFailure}
        </Alert>
      )}
      {refreshFailed ? (
        // Stated separately from the operation's own outcome. The operation
        // succeeded; it is the subsequent read that did not, and telling an
        // operator their deletion failed when it did not is worse than saying
        // nothing.
        <Alert severity="warning" role="status" sx={{ mb: 2 }} data-testid="tenants-stale">
          The change was applied, but the college list could not be refreshed afterwards, so what is
          shown here may be out of date. Any activation code just shown to you is still valid.
        </Alert>
      ) : null}

      <AsyncSection
        query={query}
        loadingLabel="Loading tenants"
        emptyTitle="No college is registered"
        emptyDescription="Register a college to record its routing and eligibility. Registration does not provision an EAI Edge appliance."
        isEmpty={(data) => data.items.length === 0}
      >
        {(data) => (
          <>
            {/* The table is wide by nature — nine columns, four of them
                  lifecycle states — so it scrolls within its own container
                  rather than forcing the page to scroll sideways. */}
            <Box sx={{ overflowX: 'auto' }} data-testid="tenants-table-scroll">
              <DataTable
                caption={`Tenants — page ${String(data.pagination.page)} of ${String(data.pagination.total_pages)}`}
                columns={columns}
                rows={data.items}
                rowKey={(row) => row.id}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {data.items.length} of {data.pagination.total_items} records (page{' '}
              {data.pagination.page} of {data.pagination.total_pages}).
            </Typography>
          </>
        )}
      </AsyncSection>

      {disclosure === null ? null : (
        <ActivationDisclosureDialog disclosure={disclosure} onClose={closeDisclosure} />
      )}

      {dialog?.kind === 'edit' ? (
        <EditCollegeDialog
          college={dialog.college}
          submitting={pending === `amend:${dialog.college.id}`}
          failure={dialogFailure}
          onSubmit={(amendment) => {
            void amend(dialog.college, amendment);
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog?.kind === 'deactivate' ? (
        <ConfirmActionDialog
          title={`Deactivate ${dialog.college.name}?`}
          summary={`${dialog.college.name} (${dialog.college.code}) will be withdrawn from service.`}
          consequences={[
            'Every usable activation code for this college is revoked.',
            'An EAI Edge claim or readiness report cannot reactivate the college. Deactivation always overrides readiness evidence.',
            'Reactivating the college is an administrative action taken separately.',
          ]}
          confirmLabel="Deactivate college"
          pendingLabel="Deactivating…"
          submitting={pending === `deactivate:${dialog.college.id}`}
          failure={dialogFailure}
          onConfirm={() => {
            void deactivate(dialog.college);
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog?.kind === 'delete' ? (
        <ConfirmActionDialog
          title={`Delete the registration for ${dialog.college.name}?`}
          summary={`${dialog.college.name} (${dialog.college.code}) will be deleted. This cannot be undone.`}
          consequences={[
            'Deletion is irreversible. The registration and its activation state are removed.',
            `The tenant identifier (${dialog.college.id}) and the college code (${dialog.college.code}) are permanently reserved and can never be registered again.`,
            'Only a college that was never claimed and never activated can be deleted. Deactivate a claimed or active college instead.',
          ]}
          confirmLabel="Delete registration"
          pendingLabel="Deleting…"
          submitting={pending === `delete:${dialog.college.id}`}
          failure={dialogFailure}
          confirmationValue={dialog.college.code}
          confirmationLabel="College code"
          onConfirm={() => {
            void remove(dialog.college);
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog?.kind === 'onboarding' ? (
        <OnboardingDetailsDialog
          tenantId={dialog.college.id}
          collegeLabel={dialog.college.name}
          onClose={closeDialog}
        />
      ) : null}
    </RequireCapability>
  );
}

/** Allocation states that are still moving, and so worth polling. */
const IN_FLIGHT_STATES: readonly string[] = [
  'requested',
  'provisioning',
  'decommission_requested',
  'decommissioning',
];
